import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { FALLiteSelectedFilters, SelectedGeolocation, SessionConstants } from '../../models/v2/search';
import { FiltersModel } from '../../models/filters';
import { FalUrlBuilder } from '../../../core/util/url/fal-url-builder';
import * as fromFalLite from '../../../features/fal-lite/+state';
import * as fromSearch from '../../../features/search/+state';
import { Observable, Subscription } from 'rxjs';
import { InstanceService } from '@commonspirit/ngx-instance';
import { ConfigurationKey } from 'src/app/core/models/configuration-key';
import { GeolocationService } from '../../services/geolocation/geolocation.service';

@Component({
  selector: 'fal-lite-search-box',
  templateUrl: './fal-lite-search-box.component.html',
  styleUrls: ['./fal-lite-search-box.component.scss']
})
export class FalLiteSearchBoxComponent implements OnDestroy, OnInit {
  @Input() keyword = '';
  @Input() selectedGeolocation: SelectedGeolocation;
  @Input() filtersModel: FiltersModel;
  @Input() falBaseUrl = '';
  @Input() geoSearchTerm = '';
  @Output() locationSelected = new EventEmitter<boolean>();
  selectedFilters$: Observable<FALLiteSelectedFilters | undefined> = new Observable<FALLiteSelectedFilters | undefined>();
  getSelectedFiltersSubscription: Subscription;
  fALLiteSelectedFilters: FALLiteSelectedFilters | undefined;
  isLocationSelected = false;

  isValidSearchGeolocation = true;
  searchDescription: any;
  defaultShowSearchButton: true;
  showSearchBtn: boolean;
  defaultSearchButton = '';
  showRoundBtn: string;
  LocationSearchActive = true;
  hideLocationSuggestions = false;
  hideKeywordSuggestions = false;
  isButtonDisabled = true;
  selectedLocation: SelectedGeolocation | null;
  enteredLocation = '';
  selectedKeyword: string | null;
  enteredKeyword = '';
  serviceTypeSelected = false;

  constructor(
    private store: Store,
    private urlBuilder: FalUrlBuilder,
    private instanceService: InstanceService,
    public geoLocationService: GeolocationService,
    private service: InstanceService
  ) {}

  ngOnInit(): void {
    sessionStorage.removeItem(SessionConstants.ReplaceKeyword);
    window.sessionStorage.removeItem(SessionConstants.SelectedSuggestion);
    this.searchDescription = this.service.getConfiguration(ConfigurationKey.ShowSearchButton)?.value;
    this.showSearchBtn = this.showSearchButton();
    this.showRoundBtn = this.getSearchButton();
  }

  showSearchButton(): boolean {
    return this.searchDescription?.iconButton ?? this.defaultShowSearchButton;
  }
  getSearchButton(): string {
    if (this.searchDescription?.enabled && this.searchDescription?.text) {
      return this.searchDescription?.text;
    }
    return this.defaultSearchButton;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/require-await
  async keywordEnterHandler(): Promise<void> {
    if (this.isValidSearch()) {
      await this.search();
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async locationEnterHandler(): Promise<void> {
    if (this.isValidSearch()) await this.search();
  }

  async search(): Promise<void> {
    const geolocationForRoute =
      this.selectedGeolocation && this.selectedGeolocation.isLocationValid
        ? this.selectedGeolocation
        : this.geoSearchTerm
        ? ({ name: this.geoSearchTerm } as SelectedGeolocation)
        : null;

    let geolocationFromQuery = null;
    if (geolocationForRoute != null)
      geolocationFromQuery = await this.geoLocationService.queryLocation(geolocationForRoute.name).toPromise();

    if (geolocationForRoute === null || geolocationFromQuery?.isLocationValid) {
      sessionStorage.setItem(SessionConstants.LocationFromRoute, JSON.stringify(geolocationFromQuery));
      const url = this.urlBuilder.searchBuild({
        isService: this.serviceTypeSelected,
        searchTerm: this.selectedKeyword ?? '',
        selectedGeolocation: geolocationForRoute
      });

      sessionStorage.setItem(
        SessionConstants.SelectedSuggestion,
        JSON.stringify({
          term: this.keyword,
          isService: this.serviceTypeSelected
        })
      );

      sessionStorage.setItem(SessionConstants.ReplaceKeyword, this.keyword);
      sessionStorage.setItem('searchUrl', url);
      this.selectedFilters$ = this.store.select(fromFalLite.setFALLiteSelectedFilters)!;
      const stringQueryParams = this.setQueryParam();
      if (location.hostname?.includes('vmfh') && !(location.pathname?.length > 1)) {
        const falPageUrl = this.instanceService.getConfiguration(ConfigurationKey.FalPage)?.value as { link: string };
        window.location.href = `${falPageUrl.link}` + url + stringQueryParams;
      } else {
        window.location.href = location.pathname.replace('/fal-lite', '') + url + stringQueryParams;
      }
    } else {
      const selectedNonValidLocation = { isLocationValid: false, name: this.geoSearchTerm } as SelectedGeolocation;
      sessionStorage.setItem(SessionConstants.LocationFromRoute, JSON.stringify(selectedNonValidLocation));
      this.isValidSearchGeolocation = false;
    }
  }
  setQueryParam(): string {
    let stringQueryParams = '';
    this.getSelectedFiltersSubscription = this.selectedFilters$.subscribe((data) => {
      this.fALLiteSelectedFilters = data;
    });
    if (this.isLocationSelected) {
      stringQueryParams = `?latitude=${this.selectedGeolocation?.latitude}&longitude=${this.selectedGeolocation?.longitude}`;
      stringQueryParams = this.fALLiteSelectedFilters?.searchDistance
        ? stringQueryParams + `&distance=${this.fALLiteSelectedFilters?.searchDistance}`
        : stringQueryParams;
    }
    if (this.fALLiteSelectedFilters?.selectedLocations) {
      stringQueryParams =
        stringQueryParams !== ''
          ? `${stringQueryParams}&locationType=${this.fALLiteSelectedFilters?.selectedLocations}`
          : `?locationType=${this.fALLiteSelectedFilters?.selectedLocations}`;
    }
    if (this.fALLiteSelectedFilters?.selectedSpecialties) {
      stringQueryParams =
        stringQueryParams !== ''
          ? `${stringQueryParams}&specialties=${this.fALLiteSelectedFilters?.selectedSpecialties}`
          : `?specialties=${this.fALLiteSelectedFilters?.selectedSpecialties}`;
    }

    return stringQueryParams;
  }

  setGeolocation(selectedGeolocation: SelectedGeolocation): void {
    this.selectedLocation = selectedGeolocation;
    this.isButtonDisabled = !this.isValidSearch();
    this.selectedGeolocation = selectedGeolocation;
    this.isLocationSelected = this.selectedGeolocation.isLocationValid;
    this.locationSelected.emit(selectedGeolocation.isLocationValid);
    this.store.dispatch(fromSearch.SearchActions.setSelectedGeolocation({ selectedGeolocation }));
  }

  geoSearchTermChanged(geoSearchTerm: string): void {
    this.geoSearchTerm = geoSearchTerm;
    this.enteredLocation = geoSearchTerm;
    this.selectedLocation = null;
    this.isButtonDisabled = !this.isValidSearch();
  }

  keywordSearchTermChanged(keyword: string): void {
    this.enteredKeyword = keyword;
    this.selectedKeyword = null;
    this.keyword = keyword;
    this.isButtonDisabled = !this.isValidSearch();
  }

  keywordsearchTermSelected(suggestionSelected: { id: string; name: string; isService: boolean }): void {
    this.selectedKeyword = suggestionSelected.id;
    this.isButtonDisabled = !this.isValidSearch();
    this.keyword = suggestionSelected.name;
    this.serviceTypeSelected = suggestionSelected.isService;
  }

  ngOnDestroy(): void {
    this.store.dispatch(fromFalLite.FalLiteActions.selectedFilterStateReset());
    this.getSelectedFiltersSubscription.unsubscribe();
  }

  settKeywordSearch(): void {
    this.hideKeywordSuggestions = !this.hideKeywordSuggestions;
  }

  setLocationSearchActive(): void {
    this.LocationSearchActive = true;
    this.hideLocationSuggestions = false;
  }

  closeLocationSuggestion(): void {
    if (this.LocationSearchActive) {
      this.hideLocationSuggestions = true;
    }
  }

  isValidSearch(): boolean {
    const isLocationValid = (this.selectedLocation != null && this.enteredLocation != '') || this.enteredLocation === '';
    const isKeywordValid = (this.selectedKeyword != null && this.enteredKeyword != '') || this.enteredKeyword === '';
    const isMixedValid =
      (isLocationValid && this.enteredKeyword === '') ||
      (isKeywordValid && this.enteredLocation === '') ||
      (this.selectedLocation != null && this.selectedKeyword != null);

    return isLocationValid && isKeywordValid && !(this.enteredLocation === '' && this.enteredKeyword === '') && isMixedValid;
  }
}
